/**
 * Universal Payment Processing Service
 * Supports multiple payment processors with a unified interface
 * Compliant with Texas dealership requirements
 */

import { supabase } from '../lib/supabase';
import { authorizeNetDirect } from './authorizeNetDirect';
// import { squarePaymentService } from './squarePaymentService'; // We'll create this later
// import { printService } from './printService'; // We'll create this later
import { notificationService } from './notificationService';

const PAYMENT_PROCESSORS = {
  AUTHORIZE_NET: 'authorize_net',
  SQUARE: 'square'
};

// Payment types for dealership
export const PAYMENT_TYPES = {
  DEPOSIT: 'deposit',
  DOWN_PAYMENT: 'down_payment',
  FULL_PAYMENT: 'full_payment',
  PARTIAL_PAYMENT: 'partial_payment',
  FINANCING_PAYMENT: 'financing_payment',
  SERVICE_PAYMENT: 'service_payment',
  PARTS_PAYMENT: 'parts_payment',
  WARRANTY_PAYMENT: 'warranty_payment',
  REFUND: 'refund'
};

// Texas tax rates
const TEXAS_TAX_RATES = {
  STATE_SALES_TAX: 0.0625, // 6.25%
  VEHICLE_SALES_TAX: 0.0625, // 6.25% for vehicles
  LOCAL_TAX_MAX: 0.02, // Up to 2% local
  TITLE_FEE: 33.00,
  REGISTRATION_FEE: 51.75,
  INSPECTION_FEE: 7.00,
  TEMP_TAG_FEE: 5.00
};

// Deposit amounts by vehicle type
const DEPOSIT_AMOUNTS = {
  vehicle: 1000.00,  // Standard $1,000 for vehicles
  'golf-cart': 0,    // Full payment required for golf carts
  boat: 2500.00,     // Higher deposit for boats
};

class PaymentProcessingService {
  constructor() {
    this.activeProcessor = null;
    this.processors = {};
    this.initializeProcessors();
  }

  /**
   * Initialize payment processors - Authorize.Net is PRIMARY
   */
  initializeProcessors() {
    this.activeProcessor = null;

    // Authorize.Net is PRIMARY processor
    if (import.meta.env.VITE_AUTHORIZE_NET_API_LOGIN_ID) {
      this.processors[PAYMENT_PROCESSORS.AUTHORIZE_NET] = {
        apiLoginId: import.meta.env.VITE_AUTHORIZE_NET_API_LOGIN_ID,
        transactionKey: import.meta.env.VITE_AUTHORIZE_NET_TRANSACTION_KEY,
        sandboxMode: import.meta.env.VITE_AUTHORIZE_NET_SANDBOX_MODE === 'true',
        type: PAYMENT_PROCESSORS.AUTHORIZE_NET
      };
      this.activeProcessor = PAYMENT_PROCESSORS.AUTHORIZE_NET;
    }
    
    // Square as fallback
    else if (import.meta.env.VITE_SQUARE_APPLICATION_ID) {
      this.processors[PAYMENT_PROCESSORS.SQUARE] = {
        applicationId: import.meta.env.VITE_SQUARE_APPLICATION_ID,
        locationId: import.meta.env.VITE_SQUARE_LOCATION_ID,
        type: PAYMENT_PROCESSORS.SQUARE
      };
      this.activeProcessor = PAYMENT_PROCESSORS.SQUARE;
    }
    
    console.log('Active payment processor:', this.activeProcessor || 'NONE');
  }

  /**
   * Process a payment using the active processor
  async processPayment(paymentData) {
    try {
      // Validate payment data
      this.validatePaymentData(paymentData);

      // OFAC compliance check - Temporarily disabled (service not yet implemented)
      // console.log('Checking OFAC compliance...');
      // const ofacCheck = await ofacService.checkCompliance(paymentData.customer);
      // 
      // if (ofacCheck.isBlocked) {
      //   throw new Error(ofacCheck.message || 'Transaction blocked for compliance reasons');
      // }
      // console.log('✅ OFAC compliance passed');

      // Calculate fees and taxes
      const paymentDetails = await this.calculatePaymentDetails(paymentData);

      // Create payment record in database
      const paymentRecord = await this.createPaymentRecord({
        ...paymentData,
        ...paymentDetails,
        processor: this.activeProcessor,
        status: 'pending'
      });

      // Process with active processor
      let result;
      switch (this.activeProcessor) {
        case PAYMENT_PROCESSORS.AUTHORIZE_NET:
          result = await this.processAuthorizeNetPayment(paymentDetails, paymentData);
          break;
        // case PAYMENT_PROCESSORS.SQUARE:
        //   result = await this.processSquarePayment(paymentDetails);
        //   break;
        default:
          throw new Error('No payment processor configured');
      }

      // Update payment record with result
      await this.updatePaymentRecord(paymentRecord.id, {
        processor_transaction_id: result.transactionId,
        status: result.success ? 'succeeded' : 'failed',
        processor_response: result
      });

      // If successful, generate invoice and trigger notifications
      if (result.success) {
        await this.postPaymentProcessing(paymentRecord, result);
      }

      return {
        success: result.success,
        paymentId: paymentRecord.id,
        transactionId: result.transactionId,
        invoice: result.invoiceUrl,
        ...result
      };

    } catch (error) {
      console.error('Payment processing error:', error);
      throw error;
    }
  }

  /**
   * Process Authorize.net payment
   */
  async processAuthorizeNetPayment(paymentDetails, paymentData) {
    try {
      console.log('Processing Authorize.Net payment...');
      
      // Check if Edge Function is available
      const useEdgeFunction = false; // SET TO TRUE AFTER DEPLOYING EDGE FUNCTION
      
      if (useEdgeFunction) {
        // SECURE: Use Edge Function (API keys on server)
        console.log('Using SECURE Edge Function for payment');
        const { data, error } = await supabase.functions.invoke('process-payment-authorizenet', {
          body: {
            amount: paymentDetails.totalAmount,
            cardNumber: paymentData.paymentMethod?.cardNumber || paymentData.cardNumber,
            expirationDate: paymentData.paymentMethod?.expirationDate || paymentData.expirationDate,
            cvv: paymentData.paymentMethod?.cvv || paymentData.cvv,
            customer: {
              name: paymentData.customer?.name || 'Customer',
              firstName: paymentData.customer?.firstName,
              lastName: paymentData.customer?.lastName,
              email: paymentData.customer?.email,
              phone: paymentData.customer?.phone,
              address: paymentData.customer?.address,
              city: paymentData.customer?.city || 'Paradise',
              state: paymentData.customer?.state || 'TX',
              zip: paymentData.customer?.zip || '76073'
            }
          }
        });
        
        if (error) throw error;
        
        return {
          success: true,
          transactionId: data.transId,
          authCode: data.authCode,
          avsResponse: data.avsResultCode
        };
      } else {
        // INSECURE: Direct API (ONLY FOR TESTING - DO NOT USE IN PRODUCTION!)
        console.warn('⚠️ USING INSECURE DIRECT API - DEPLOY EDGE FUNCTION ASAP!');
        console.warn('📖 See DEPLOY-EDGE-FUNCTIONS.md for instructions');
        
        const result = await authorizeNetDirect.processPayment({
          amount: paymentDetails.totalAmount,
          cardNumber: paymentData.paymentMethod?.cardNumber || paymentData.cardNumber,
          expirationDate: paymentData.paymentMethod?.expirationDate || paymentData.expirationDate,
          cvv: paymentData.paymentMethod?.cvv || paymentData.cvv,
          customer: {
            name: paymentData.customer?.name || 'Customer',
            firstName: paymentData.customer?.firstName,
            lastName: paymentData.customer?.lastName,
            email: paymentData.customer?.email,
            phone: paymentData.customer?.phone,
            address: paymentData.customer?.address,
            city: paymentData.customer?.city || 'Paradise',
            state: paymentData.customer?.state || 'TX',
            zip: paymentData.customer?.zip || '76073'
          }
        });
        
        if (result.success) {
          console.log('✅ Payment successful (INSECURE MODE):', result.transactionId);
        } else {
          console.error('❌ Payment failed:', result.error);
        }
        
        return result;
      }
      
    } catch (error) {
      console.error('Authorize.Net payment exception:', error);
      return {
        success: false,
        error: error.message || 'Payment processing failed'
      };
    }
  }

  /**
   * Process Square payment
   */
  async processSquarePayment(paymentDetails) {
    try {
      const { data, error } = await supabase.functions.invoke('process-payment-square', {
        body: {
          amount: Math.round(paymentDetails.totalAmount * 100), // Convert to cents
          sourceId: paymentDetails.paymentMethod.sourceId,
          customer: paymentDetails.customer,
          note: paymentDetails.description
        }
      });

      if (error) throw error;

      return {
        success: true,
        transactionId: data.payment.id,
        receiptUrl: data.payment.receiptUrl
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Calculate payment details including taxes and fees
   */
  async calculatePaymentDetails(paymentData) {
    const { amount, type, vehicleId, includeTexasFees = true } = paymentData;
    
    let baseAmount = parseFloat(amount);
    let taxAmount = 0;
    let feesAmount = 0;
    let totalAmount = baseAmount;

    // Calculate sales tax for vehicle purchases
    if (['full_payment', 'down_payment'].includes(type) && vehicleId) {
      taxAmount = baseAmount * TEXAS_TAX_RATES.STATE_SALES_TAX;
      
      // Add local tax if applicable
      if (paymentData.localTaxRate) {
        taxAmount += baseAmount * (paymentData.localTaxRate / 100);
      }
    }

    // Add Texas DMV fees for vehicle purchases
    if (type === 'full_payment' && includeTexasFees && vehicleId) {
      feesAmount += TEXAS_TAX_RATES.TITLE_FEE;
      feesAmount += TEXAS_TAX_RATES.REGISTRATION_FEE;
      feesAmount += TEXAS_TAX_RATES.INSPECTION_FEE;
      
      // Add temp tag fee if needed
      if (paymentData.needsTempTag) {
        feesAmount += TEXAS_TAX_RATES.TEMP_TAG_FEE;
      }
    }

    totalAmount = baseAmount + taxAmount + feesAmount;

    return {
      baseAmount,
      taxAmount,
      feesAmount,
      totalAmount,
      invoiceNumber: await this.generateInvoiceNumber(),
      metadata: {
        type,
        vehicleId,
        breakdown: {
          base: baseAmount,
          tax: taxAmount,
          titleFee: includeTexasFees ? TEXAS_TAX_RATES.TITLE_FEE : 0,
          registrationFee: includeTexasFees ? TEXAS_TAX_RATES.REGISTRATION_FEE : 0,
          inspectionFee: includeTexasFees ? TEXAS_TAX_RATES.INSPECTION_FEE : 0,
          tempTagFee: paymentData.needsTempTag ? TEXAS_TAX_RATES.TEMP_TAG_FEE : 0
        }
      }
    };
  }

  // OFAC compliance is now handled by ofacService.js
  // No Edge Functions required - uses free US Trade.gov API

  /**
   * Create payment record in database
   */
  async createPaymentRecord(paymentData) {
    const { data, error } = await supabase
      .from('payments')
      .insert({
        amount: paymentData.totalAmount || paymentData.amount,
        base_amount: paymentData.baseAmount || paymentData.amount,
        tax_amount: paymentData.taxAmount || 0,
        fees_amount: paymentData.feesAmount || 0,
        type: paymentData.type,
        processor: paymentData.processor,
        vehicle_id: paymentData.vehicleId,
        customer_name: paymentData.customer.name,
        customer_email: paymentData.customer.email,
        customer_phone: paymentData.customer.phone,
        customer_address: paymentData.customer.address,
        invoice_number: paymentData.invoiceNumber,
        status: paymentData.status,
        metadata: paymentData.metadata,
        ip_address: paymentData.customer.ipAddress,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Update payment record
   */
  async updatePaymentRecord(paymentId, updates) {
    const { error } = await supabase
      .from('payments')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', paymentId);
  }

  /**
   * Post-payment processing (receipts, notifications, etc.)
   */
  async postPaymentProcessing(paymentRecord, paymentResult) {
    try {
      // Generate invoice
      const invoice = await this.generateInvoice(paymentRecord, paymentResult);
      
      // Send notifications
      await this.sendPaymentNotification(paymentRecord, invoice);
      
      // Send email notification to owner for deposits and purchases
      if (paymentRecord.type === PAYMENT_TYPES.DEPOSIT || paymentRecord.type === PAYMENT_TYPES.FULL_PAYMENT) {
        await notificationService.sendDepositNotification({
          customer: {
            name: paymentRecord.customer_name,
            email: paymentRecord.customer_email,
            phone: paymentRecord.customer_phone,
            address: paymentRecord.customer_address,
            city: paymentRecord.customer_city,
            state: paymentRecord.customer_state,
            zip: paymentRecord.customer_zip
          },
          vehicle: paymentRecord.vehicle,
          amount: paymentRecord.amount,
          type: paymentRecord.type,
          transactionId: paymentResult.transactionId
        });
      }
      
      // Generate Texas-specific forms if needed
      if (paymentRecord.type === PAYMENT_TYPES.FULL_PAYMENT) {
        await this.generateTexasDMVForms(paymentRecord);
      }
      
      // Create audit log
      await this.createAuditLog('payment_processed', paymentRecord);
      
    } catch (error) {
      console.error('Post-payment processing error:', error);
      // Don't throw - payment was successful even if follow-up fails
    }
  }

  /**
   * Generate invoice number
   */
  async generateInvoiceNumber() {
    const prefix = 'PP'; // Paradise-Performance
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    
    // Get count of invoices this month
    const { count } = await supabase
      .from('payments')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', `${year}-${month}-01`)
      .lt('created_at', `${year}-${String(parseInt(month) + 1).padStart(2, '0')}-01`);
    
    const sequence = String((count || 0) + 1).padStart(5, '0');
    return `${prefix}-${year}${month}-${sequence}`;
  }

  /**
   * Generate invoice/receipt PDF
   */
  async generateInvoice(paymentRecord, paymentResult) {
    try {
      const { data, error } = await supabase.functions.invoke('generate-invoice', {
        body: {
          payment: paymentRecord,
          transaction: paymentResult,
          template: 'texas_dealership_invoice'
        }
      });

      if (error) throw error;

      // Store invoice URL in payment record
      await this.updatePaymentRecord(paymentRecord.id, {
        invoice_url: data.invoiceUrl,
        receipt_url: data.receiptUrl
      });

      return data;
    } catch (error) {
      console.error('Invoice generation failed:', error);
      return null;
    }
  }

  /**
   * Update vehicle status after payment
   */
  async updateVehicleStatus(paymentRecord) {
    let newStatus;
    switch (paymentRecord.type) {
      case 'deposit':
        newStatus = 'deposit_paid';
        break;
      case 'full_payment':
        newStatus = 'sold';
        break;
      case 'down_payment':
        newStatus = 'financing_in_progress';
        break;
      default:
        return;
    }

    await supabase
      .from('vehicles')
      .update({
        status: newStatus,
        payment_status: newStatus,
        last_payment_date: new Date().toISOString(),
        last_payment_amount: paymentRecord.amount
      })
      .eq('id', paymentRecord.vehicle_id);
  }

  /**
   * Send payment notification email
   */
  async sendPaymentNotification(paymentRecord, invoice) {
    try {
      await supabase.functions.invoke('send-email', {
        body: {
          to: paymentRecord.customer_email,
          subject: `Payment Confirmation - ${paymentRecord.invoice_number}`,
          template: 'payment_confirmation',
          data: {
            customerName: paymentRecord.customer_name,
            invoiceNumber: paymentRecord.invoice_number,
            amount: paymentRecord.amount,
            type: paymentRecord.type,
            invoiceUrl: invoice?.invoiceUrl,
            receiptUrl: invoice?.receiptUrl
          }
        }
      });
    } catch (error) {
      console.error('Failed to send payment notification:', error);
    }
  }

  /**
   * Generate Texas DMV forms
   */
  async generateTexasDMVForms(paymentRecord) {
    try {
      // Generate Form 130-U
      await supabase.functions.invoke('generate-dmv-form', {
        body: {
          formType: 'form_130u',
          paymentId: paymentRecord.id,
          vehicleId: paymentRecord.vehicle_id,
          customerId: paymentRecord.customer_id
        }
      });

      // Check if other forms are needed based on transaction type
      // This would be expanded based on specific requirements
    } catch (error) {
      console.error('Failed to generate DMV forms:', error);
    }
  }

  /**
   * Create audit log entry
   */
  async createAuditLog(action, data) {
    try {
      await supabase.from('audit_logs').insert({
        action,
        entity_type: 'payment',
        entity_id: data.id,
        data: JSON.stringify(data),
        user_id: data.user_id,
        ip_address: data.ip_address,
        created_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to create audit log:', error);
    }
  }

  /**
   * Validate payment data
   */
  validatePaymentData(paymentData) {
    const required = ['amount', 'type', 'customer'];
    
    for (const field of required) {
      if (!paymentData[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }

    if (!paymentData.customer.email || !paymentData.customer.name) {
      throw new Error('Customer email and name are required');
    }

    if (paymentData.amount <= 0) {
      throw new Error('Payment amount must be greater than 0');
    }

    // Validate vehicle-specific requirements
    if (paymentData.vehicleId && paymentData.type === 'deposit') {
      const vehicleType = paymentData.vehicleType || 'vehicle';
      if (vehicleType === 'golf-cart') {
        throw new Error('Golf carts require full payment, not deposits');
      }
    }
  }

  /**
   * Get deposit amount for vehicle type
   */
  getDepositAmount(vehicleType) {
    return DEPOSIT_AMOUNTS[vehicleType] || DEPOSIT_AMOUNTS.vehicle;
  }
  /**
   * Calculate financing terms
   */
  calculateFinancingTerms(principal, apr, termMonths, downPayment = 0) {
    const loanAmount = principal - downPayment;
    const monthlyRate = apr / 100 / 12;
    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                          (Math.pow(1 + monthlyRate, termMonths) - 1);
    const totalPayments = monthlyPayment * termMonths;
    const totalInterest = totalPayments - loanAmount;

    return {
      loanAmount,
      monthlyPayment: Math.round(monthlyPayment * 100) / 100,
      totalPayments: Math.round(totalPayments * 100) / 100,
      totalInterest: Math.round(totalInterest * 100) / 100,
      apr,
      termMonths
    };
  }

  /**
   * Process refund
   */
  async processRefund(originalPaymentId, refundAmount, reason) {
    try {
      // Get original payment
      const { data: originalPayment, error } = await supabase
        .from('payments')
        .select('*')
        .eq('id', originalPaymentId)
        .single();

      if (error || !originalPayment) {
        throw new Error('Original payment not found');
      }

      // Validate refund amount
      if (refundAmount > originalPayment.amount) {
        throw new Error('Refund amount cannot exceed original payment amount');
      }

      // Process refund with appropriate processor
      let refundResult;
      switch (originalPayment.processor) {
        case PAYMENT_PROCESSORS.AUTHORIZE_NET:
          refundResult = await this.processAuthorizeNetRefund(originalPayment, refundAmount);
          break;
        case PAYMENT_PROCESSORS.SQUARE:
          refundResult = await this.processSquareRefund(originalPayment, refundAmount);
          break;
        default:
          throw new Error('Unknown payment processor');
      }

      // Create refund record
      await this.createPaymentRecord({
        amount: -refundAmount,
        type: PAYMENT_TYPES.REFUND,
        processor: originalPayment.processor,
        original_payment_id: originalPaymentId,
        customer: {
          name: originalPayment.customer_name,
          email: originalPayment.customer_email,
          phone: originalPayment.customer_phone
        },
        metadata: {
          reason,
          original_invoice: originalPayment.invoice_number,
          refund_transaction_id: refundResult.refundId
        },
        status: refundResult.success ? 'succeeded' : 'failed'
      });

      return refundResult;
    } catch (error) {
      console.error('Refund processing error:', error);
      throw error;
    }
  }

  /**
   * Get payment history for customer
   */
  async getCustomerPaymentHistory(customerEmail) {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('customer_email', customerEmail)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  /**
   * Get payment analytics
   */
  async getPaymentAnalytics(startDate, endDate) {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .gte('created_at', startDate)
      .lte('created_at', endDate)
      .eq('status', 'succeeded');

    if (error) throw error;

    // Calculate analytics
    const totalRevenue = data.reduce((sum, p) => sum + p.amount, 0);
    const totalTransactions = data.length;
    const averageTransaction = totalRevenue / totalTransactions;
    
    const byType = data.reduce((acc, p) => {
      acc[p.type] = (acc[p.type] || 0) + p.amount;
      return acc;
    }, {});

    const byProcessor = data.reduce((acc, p) => {
      acc[p.processor] = (acc[p.processor] || 0) + 1;
      return acc;
    }, {});

    return {
      totalRevenue,
      totalTransactions,
      averageTransaction,
      byType,
      byProcessor,
      payments: data
    };
  }
}

// Export singleton instance
export const paymentProcessingService = new PaymentProcessingService();

// Export for use in other components
export default paymentProcessingService;
